/**
 * scenarioParamUtil.js
 *
 * 시나리오 파라미터 편집기(RP/IP/MP/DP) 공용 순수 함수 + 공용 행 스타일.
 *
 * "변경(전송) 판정" 규칙 — 유효값(non-empty)이 있는 파라미터만 엔진에 전송한다.
 *   1) DB 초기값 없음 + 미입력            → 값 없음 → 미전송
 *   2) DB 초기값 있음 + 미변경            → 값 있음 → 전송(적용)
 *   3) DB 초기값 있음 + 의도적 clear      → 값 없음 → 미전송
 *   4) DB 초기값 없음 + 사용자 입력       → 값 있음 → 전송
 * baseline 평균과의 수치 비교는 하지 않는다(빈값 드롭만).
 *
 * "접기/펼치기 기본값" — 시드 초기값(DB)이 있는 파라미터만 펼친 상태로 시작한다.
 */
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

// ── 퍼센트 표시 변환 (카탈로그 meta display="percent" 파라미터 공용) ──────────────
// 저장 정본은 소수/배수(효율 1.2 / 수율 0.97), 표시·입력만 %(120% / 97%).
//   화면 표시: 소수 → %(×100). 입력 저장: % → 소수(÷100). 빈 값은 "" 유지.
//   (기존 mpScenarioParamUtil.yieldDecToPct / AiMpScenarioPlanning.yieldPctToDec 와 동일 식의 범용판.)
export const decToPct = (dec) =>
  (dec == null || dec === "") ? "" : Number((Number(dec) * 100).toFixed(2));
export const pctToDec = (pct) =>
  (pct == null || pct === "") ? "" : Number((Number(pct) / 100).toFixed(4));

// ── 파라미터 행 공용 스타일 (RP/IP/MP/DP/BF 5개 편집기 단일 소스) ──────────────
// 평면 리스트: 행마다 상자를 두르지 않고 하단 hairline 으로만 구분한다.
//   - 배경 틴트 + 좌측 3px 바 = "이 파라미터가 조정됐다"는 **파라미터 단위** 신호.
//     (과거처럼 섹션 전체를 칠하지 않는다 — 섹션 틴트는 신호를 희석시킨다.)
//   - 미변경 행은 배경·테두리 없음.
// 단면 테두리(border-left)와 큰 radius 는 같이 쓰면 어색하므로 모서리는 3px 로 억제한다.
export const PARAM_ROW_RADIUS = "3px";
export const PARAM_CARD_RADIUS = "4px";   // 대상 선택 카드·빈상태 박스 등(테두리는 유지, 각지게만)
export const PARAM_ROW_PAD_X = "9px";     // 헤더·본문 좌우 패딩 공통(본문이 헤더와 정렬되도록)

// 행 헤더 **고정** 높이. minHeight 로 두면 컨트롤마다 실제 높이가 미묘하게 달라(세그먼트·select 의
//   내부 패딩·line-height) 클릭할 때마다 행이 출렁인다. 안에 무엇이 들어오든 높이를 상수로 잠근다.
//   되돌리기 버튼(22)이 행에서 가장 큰 요소이고 편집 컨트롤도 22 로 맞춰 두었다 → 22 + 패딩 12 = 34.
export const PARAM_ROW_MIN_H = 34;
// 행 안 편집 컨트롤(입력·select·세그먼트·아이콘 버튼) 공통 높이.
export const PARAM_CTRL_H = 22;

// 숫자 입력칸 고정 폭. 카탈로그상 number/multiplier 값은 최대 4자(-10 ~ 100, 0.96 등)이고
// 상한이 없는 건 MP lot_size 뿐이라 6자리 + 스피너가 들어가는 92px 로 충분하다.
// 단위(개월/시간/EA/배)는 칸 밖 오른쪽 라벨이므로 이 폭에 포함되지 않는다.
// select(최장 옵션 18자)·text(DP 수식)·계층 선택기는 그대로 full width.
export const PARAM_NUM_INPUT_W = 92;

// select 는 고정 폭이 아니라 auto — 네이티브 select 의 auto 폭은 **가장 긴 옵션** 기준이라
// 선택 값을 바꿔도 폭이 흔들리지 않고, 언어를 바꿔도(번역 길이가 달라져도) 잘리지 않는다.
// 카드보다 긴 옵션(RP `기준 재고 (Base Stock)` 18자)만 maxWidth 로 잘라 준다.
// (MP 공정별 정책 select 가 쓰던 방식을 파라미터 select 전반으로 넓힌 것.)
export const PARAM_SELECT_W = { width: "auto", maxWidth: "100%" };

// 파라미터 "값"을 고르는 2택 세그먼트 스타일 — 각진 분절 버튼.
//   공용 SegmentedControl 의 기본형(둥근 알약 + 회색 트랙)은 "보기 방식 전환"(조정만/전체 같은
//   화면 상태 토글)에 쓰는 관용구라, 값 입력에 그대로 쓰면 같은 카드 안에서 역할이 헷갈린다.
//   여기서는 입력 컨트롤처럼 보이도록 모서리를 죽이고 칸을 세로선으로 나눈다.
export const PARAM_TOGGLE_SX = {
  bgcolor: "transparent", p: 0, gap: 0, overflow: "hidden",
  borderRadius: PARAM_ROW_RADIUS, border: `1px solid ${C.border}`,
  // 숫자 입력·select 와 같은 높이(22)로 맞춘다. 이 컨트롤은 button 이라 값 셀의
  //   `& input, & select` 높이 규칙에 안 걸려, 여기서 직접 고정하지 않으면 행이 늘어난다.
  height: 22, boxSizing: "border-box",
  "&& .MuiToggleButtonGroup-grouped": {
    // 공용 SegmentedControl 이 알약 모양을 `16px !important` 로 박아 둬서 특이성만으로는 못 이긴다.
    borderRadius: "0 !important", m: 0, border: 0,
    height: "100%", boxSizing: "border-box",
    fontSize: FONT.CAPTION, fontWeight: 700, px: "10px", py: 0, lineHeight: 1,
    color: C.text2,
    "&:not(:first-of-type)": { borderLeft: `1px solid ${C.border}` },
    "&.Mui-selected": { boxShadow: "none" },
  },
};

// 행을 펼치지 않고 값 셀에서 바로 고칠 수 있는 타입.
//   펼침 본문에 입력 컨트롤 하나만 들어가는 타입들이라 아코디언이 껍데기였다.
//   여기 없는 타입(DP 거래처 계층 트리, MP 자원 그리드·기간·휴무 캘린더, BF 위젯)은
//   대상 선택·기간 등 여러 층이 필요해 펼침을 유지한다.
const INLINE_EDIT_TYPES = new Set(["number", "multiplier", "select", "toggle"]);
export const isInlineEditable = (def) => INLINE_EDIT_TYPES.has(def?.type);

// 절대값이 아니라 "현재값 대비 증감"을 입력하는 파라미터인가.
//   정본은 백엔드 카탈로그의 signed 플래그(base/catalog.py 가 param_defs 로 방출).
//   `*Delta` 접미어는 그 플래그를 싣지 않던 캐시 응답용 폴백.
//   쓰임새는 접힌 행 요약뿐 — 입력칸 옆 부호 표시는 `- (-2%)` 처럼 부호가 두 번 보여 폐기했다.
export const isSignedParam = (def) =>
  !!def?.signed || /Delta$/.test(def?.param_key || "");

// @param accent  시나리오 role 색
// @param applied 유효값 보유(엔진 전송 대상) — 조정된 행
// @param hot     Agent Chat set_param 직후 강조 — applied 보다 우선
// @param last    그룹 마지막 행(하단 구분선 생략)
export function paramRowSx({ accent, applied, hot, last }) {
  const mark = hot ? C.acc : applied ? accent : "transparent";
  return {
    borderRadius: PARAM_ROW_RADIUS,
    overflow: "hidden",
    borderLeft: `3px solid ${mark}`,
    borderBottom: last ? "none" : `0.5px solid ${C.border}`,
    bgcolor: hot ? `${C.acc}10` : applied ? `${accent}0A` : "transparent",
    ...(hot ? { boxShadow: `0 0 0 2px ${C.acc}12` } : {}),
  };
}

// 그룹/섹션 헤더 — 배경·테두리 없이 라벨 + 우측 hairline rule 로만 구분.
export const paramGroupHeaderSx = { display: "flex", alignItems: "center", gap: 0.6, mb: 0.2 };
export const paramGroupRuleSx   = { flex: 1, height: "1px", bgcolor: C.border };

// 유효값(전송 대상) 여부. def 는 array 타입 판별 외에는 미사용(시그니처 호환).
export function hasEffectiveValue(def, value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;   // DP compute_measure_formula 등
  return String(value).trim() !== "";
}

// 시나리오 시드 초기값(DB param_overrides)이 있는 param_key Set — 펼침 기본값.
//  - RP/IP : overrides[].params 의 키(유효값 보유)
//  - MP    : overrides[].param_key (per-param 엔트리)
export function seededParamKeys(scenario) {
  const keys = new Set();
  const ovs = Array.isArray(scenario?.overrides) ? scenario.overrides : [];
  for (const o of ovs) {
    if (o == null) continue;
    if (o.param_key) keys.add(o.param_key);            // MP per-param
    if (o.params && typeof o.params === "object") {
      for (const [k, v] of Object.entries(o.params)) {
        if (hasEffectiveValue(null, v)) keys.add(k);
      }
    }
  }
  return keys;
}

// RP/IP override 행 목록 → 빈값 키 제거. params 가 비면 행 자체 드롭. MP per-param 엔트리는
// 유효 value 없으면 드롭. 원본 불변(새 배열/객체).
export function pruneOverridesForEngine(overrides) {
  if (!Array.isArray(overrides)) return overrides;
  const out = [];
  for (const o of overrides) {
    if (o == null) continue;
    if (o.param_key) {                                 // MP per-param 엔트리
      // 기간별 능력: 유효 기간 행(start_dt·end_dt·value 모두 채움)만 추려 보존.
      const periods = Array.isArray(o.periods)
        ? o.periods.filter(p => p
            && hasEffectiveValue(null, p.value)
            && hasEffectiveValue(null, p.start_dt)
            && hasEffectiveValue(null, p.end_dt))
        : [];
      // 자원별 개별 값(targets[].value) / 공정별 정책(route_policies) — value 없이 전송(생산 공정 상세 routing_op).
      const targetVals = Array.isArray(o.targets)
        && o.targets.some(t => hasEffectiveValue(null, t?.value));
      // 자원별 기간별 능력(targets[].periods) — value 없이 기간 행만 있어도 전송(능력 역산 적용 등).
      //   ⚠ 이 검사 없으면 period 모드(value 공백) override 가 drop 되어 plan_config_obj 에 반영 안 됨.
      const targetPeriods = Array.isArray(o.targets)
        && o.targets.some(t => Array.isArray(t?.periods)
            && t.periods.some(p => p && hasEffectiveValue(null, p.value)
                && hasEffectiveValue(null, p.start_dt) && hasEffectiveValue(null, p.end_dt)));
      const routePols = o.route_policies && Object.keys(o.route_policies).length > 0;
      // 수요 우선순위 순위 빌더(priority_items) — value 없이 기준 목록만 있어도 전송.
      const priorityItems = Array.isArray(o.priority_items) && o.priority_items.length > 0;
      // 비-전체 스코프인데 대상(targets)이 없으면 값·기간은 적용 대상이 없어 무의미 → 드롭.
      //   route_policies/priority_items 는 target 없이도 유효한 페이로드라 예외(아래 분기에서 별도 통과).
      const scopedNoTargets = o.scope_type && o.scope_type !== "all"
        && !(Array.isArray(o.targets) && o.targets.length > 0);
      if (periods.length > 0 && !scopedNoTargets) {
        out.push({ ...o, periods });                   // 기간 행 있으면 value 비어도 전송
      } else if (targetVals || targetPeriods || routePols || priorityItems) {
        const rest = { ...o };                          // 대상별 값/정책/우선순위 목록 보유 → 그대로 전송(빈 periods 제거)
        delete rest.periods;
        out.push(rest);
      } else if (hasEffectiveValue(null, o.value) && !scopedNoTargets) {
        const rest = { ...o };                          // 유효 기간 없으면 빈 periods 제거 후 value 만
        delete rest.periods;
        out.push(rest);
      }
      continue;
    }
    if (o.params && typeof o.params === "object") {
      const params = {};
      for (const [k, v] of Object.entries(o.params)) {
        if (hasEffectiveValue(null, v)) params[k] = v;
      }
      if (Object.keys(params).length === 0) continue;  // 변경분 없는 행 드롭
      out.push({ ...o, params });
      continue;
    }
    out.push(o);
  }
  return out;
}

// DP/제너릭 전역 params → 빈값 키 제거. 원본 불변.
export function pruneParamsForEngine(params) {
  if (!params || typeof params !== "object") return params;
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (hasEffectiveValue(null, v)) out[k] = v;
  }
  return out;
}

// ── 구성 카드 → 커스텀 시나리오 저장용 평탄 params ────────────────────────────────
// 시나리오 구성 탭에서 편집한 카드 1장을 z_ai_scenario(source_type='USER') 저장 형태로 접는다.
// override 형태 3종을 모두 처리한다:
//   - RP/IP  : overrides[] = [{ scope_type, loc_cd, item_cd, inv_class_cd, params:{k:v} }]
//   - MP     : overrides[] = [{ param_key, scope_type, value, targets[], periods[], ... }] (per-param 엔트리)
//   - 레거시 : sc.params = {k:v} (DP/BF)
// 빈값 제외는 hasEffectiveValue 단일 규칙. 같은 키가 여러 행에 있으면 배열 뒤 행이 이긴다.
// 값 단위는 변환하지 않는다 — 카드 override 값은 이미 서버 정본(소수/배수)이다.
//   (MP 위젯은 표시만 %, 저장은 소수. ScenarioManage 의 percent 변환은 그 화면 전용 표시 로직이다.)
//
// TODO(스코프 보존): z_ai_scenario 는 시나리오당 단일 scope_type + 평탄 params 구조라
//   거점/품목/분류 한정 override 와 MP 의 대상별·기간별 설정을 그대로 담을 수 없다. 보존하려면
//   z_ai_scenario 에 스코프 행 배열 컬럼(예: scope_rows JSONB) 추가 + Python 저장/조회 경로
//   (scenario_registry.insert_user_scenario / list_user_scenarios) 수정이 선행돼야 한다.
//   현재는 scope_type='all' 로 접어 저장하고, 아래 진단값으로 화면에서 손실을 경고한다.
//
// @returns {{ params: Object, scopeLost: boolean, unsupportedKeys: string[] }}
//   scopeLost      : override 행이 2개 이상이거나 scope_type 이 all 이 아닌 행이 있음
//   unsupportedKeys: 평탄 dict 로 표현 불가한 param_key (MP targets/periods/route_policies/priority_items)
export function flattenScenarioParamsForCustomSave(sc) {
  const out = {};
  const unsupportedKeys = [];
  // 빈 배열은 "조정한 스코프가 없다"는 뜻이지 "저장할 값이 없다"가 아니다.
  // BF 처럼 편집 정본이 params 하나인 모듈은 overrides 가 항상 비어 있어,
  //   길이를 보지 않으면 사용자가 채운 값을 통째로 버리고 빈 시나리오를 저장한다.
  const ovs = Array.isArray(sc?.overrides) && sc.overrides.length ? sc.overrides : null;
  if (!ovs) {
    for (const [k, v] of Object.entries(sc?.params || {})) {
      if (hasEffectiveValue(null, v)) out[k] = v;
    }
    return { params: out, scopeLost: false, unsupportedKeys };
  }
  for (const o of ovs) {
    if (o == null) continue;
    if (o.param_key) {                                   // MP per-param 엔트리
      if (hasEffectiveValue(null, o.value)) out[o.param_key] = o.value;
      const hasTargets = Array.isArray(o.targets) && o.targets.length > 0;
      const hasPeriods = Array.isArray(o.periods) && o.periods.length > 0;
      const hasRoutes  = o.route_policies && Object.keys(o.route_policies).length > 0;
      const hasPriority = Array.isArray(o.priority_items) && o.priority_items.length > 0;
      if (hasTargets || hasPeriods || hasRoutes || hasPriority) unsupportedKeys.push(o.param_key);
      continue;
    }
    if (o.params && typeof o.params === "object") {      // RP/IP 스코프 행
      for (const [k, v] of Object.entries(o.params)) {
        if (hasEffectiveValue(null, v)) out[k] = v;      // 뒤 행이 앞 행을 덮어쓴다
      }
    }
  }
  const scopeLost = ovs.length > 1
    || ovs.some(o => o && o.scope_type && o.scope_type !== "all");
  return { params: out, scopeLost, unsupportedKeys };
}

// ── DP INITIAL_DEMAND 레거시 맵 → "사용자 정의(layout)" 형태 변환 ─────────────────
// 레거시 맵({초기화방식: 수식[], rollingRange, ...})은 엔진이 "맵의 키 → initialDemand"로
// 해석하는데, 2026-07 엔진 회귀(#9678)로 그 해석이 사라져 수요 적재(makeDemand)가 500 이 된다.
// 화면 "수요계획 초기값=사용자 정의"가 쓰는 layout 경로는 회귀와 무관하게 동작하므로,
// AI 플로우의 makeDemand payload 를 이 형태로 변환해 보낸다 (저장된 버전 설정은 원형 유지).
//   { computeFormula: 수식[], ...메타키, defaultValue: 초기화방식, layout: [] }
// 키 순서 중요: computeFormula(미지 키 → 수식 해석) 먼저, layout(초기화방식 확정) 마지막.
// 엔진 회귀가 수정돼도 이 형태는 동일하게 동작한다. 이미 layout 형태면 그대로 반환.
export function toDpLayoutInitialDemand(init) {
  if (!init || typeof init !== "object" || Array.isArray(init) || init.layout !== undefined) return init;
  const META = new Set(["rollingRange", "custLevelId", "itemLevelId", "custLevel", "itemLevel",
                        "defaultValue", "copyTarget"]);
  let dv = null, formula = [];
  const meta = {};
  for (const [k, v] of Object.entries(init)) {
    if (META.has(k)) meta[k] = v;
    else if (dv == null) { dv = k; formula = Array.isArray(v) ? v : []; }
  }
  if (!dv) return init;   // 초기화 방식 키가 없으면 해석 불가 — 원형 유지
  return { computeFormula: formula, ...meta, defaultValue: dv, layout: [] };
}

// makeDemand payload 용 — planConfigObj.INITIAL_DEMAND 만 layout 형태로 바꾼 사본 반환.
export function withDpLayoutInitialDemand(versionDto) {
  const cfg = versionDto?.planConfigObj;
  if (!cfg || cfg.INITIAL_DEMAND == null) return versionDto;
  return { ...versionDto,
           planConfigObj: { ...cfg, INITIAL_DEMAND: toDpLayoutInitialDemand(cfg.INITIAL_DEMAND) } };
}

// 모듈 전체 파라미터 카탈로그 기억소.
// fetchTemplates 는 응답의 scenarios 만 배열로 바꿔 돌려주므로 최상위 키가 그대로 버려진다.
// 표준 시나리오가 0건인 테넌트는 그 배열이 비어 실을 자리조차 없다.
// 그래서 모듈 싱글턴에 따로 남겨 직접 입력 카드의 폴백으로 쓴다.
// 수명은 dynTemplates 캐시(redux viewStates, 비영속)와 같아 새로고침 시 둘 다 비고 재조회된다.
// SPA 는 세션당 단일 테넌트라 테넌트 교차 오염이 없다.
export function rememberModuleCatalog(mod, res) {
  if (!mod) return;
  mod._moduleCatalog = {
    paramDefs:      Array.isArray(res?.param_defs) ? res.param_defs : [],
    scopeTargets:   res?.scope_targets   || {},
    problemTargets: res?.problem_targets || {},
  };
}

// 기억해 둔 모듈 카탈로그. 조회 전이거나 카탈로그가 비면 null.
export function moduleCatalogFallback(mod) {
  const c = mod?._moduleCatalog;
  return c?.paramDefs?.length ? c : null;
}

// ── 카탈로그 카드 → 파라미터 변경 요약 ────────────────────────────────────────
// 표준 시나리오 카드의 hover 미리보기와 커스텀 저장 팝업이 함께 쓰는 정규화 계층.
// i18n·스타일·모듈 훅을 모르는 순수 함수로 유지한다 — 표시는 scenarioParamCells 가 맡는다.

// 카탈로그 카드(tpl)에서 카드 클릭 시 시드로 쓰이는 변형 1건을 고른다.
// makeAddedScenario 와 미리보기가 같은 함수를 봐야 "보이는 것 = 추가되는 것" 이 유지된다.
// alt1(주 변형) 우선, 없으면 첫 비-base 변형, 그것도 없으면 null(=미리보기할 변경이 없다).
export function pickSeedVariant(tpl) {
  const variants = tpl?.scenarios || [];
  return variants.find(v => v.role === "alt1")
      || variants.find(v => v.role && v.role !== "base")
      || null;
}

// param_key → def 색인. 열거 순서가 그대로 표시 순서가 된다(카탈로그 정본 순 = 편집기 행 순서).
// 카탈로그 param_defs 를 기본으로 하고, 레거시 mod.params(DP/BF 배열 스펙)를 폴백으로 얹는다.
//   주의: 카탈로그 def 의 키 필드는 param_key 고 레거시는 paramKey|k 다. 조인에 실패하면 코드 원문이 노출된다.
// 서버 param_defs 는 JSON 이라 format 함수를 담지 못한다.
// 배열·객체값(BF 알고리즘·외부 인자·미래값 설정)은 그 함수가 없으면 "AR,HW" 나 [object Object] 로
//   노출되므로, 카탈로그 def 가 이겼더라도 표시 규칙만 얹어 준다.
// 인자는 param_defs 를 가진 어떤 객체든 받는다 — 카탈로그 변형과 구성 카드 인스턴스 양쪽이 쓴다.
export function buildParamDefIndex(sc, mod) {
  const m = {};
  for (const d of (sc?.param_defs || [])) {
    const k = d?.param_key ?? d?.key;
    if (k) m[k] = d;
  }
  // mod.params 는 배열 스펙이다. 형태가 어긋난 모듈이 생겨도 미리보기가 죽지 않게 막는다 —
  //   이 함수는 카드 hover 마다 돌아서, 순회 오류가 나면 카탈로그 전체가 못 뜬다.
  for (const d of (Array.isArray(mod?.params) ? mod.params : [])) {
    const k = d?.paramKey ?? d?.k;
    if (!k) continue;
    if (!m[k]) { m[k] = { ...d, param_key: k, label: d.label ?? d.k }; continue; }
    if (typeof d.format === "function" && typeof m[k].format !== "function") {
      m[k] = { ...m[k], format: d.format };
    }
  }
  return m;
}

// override 1건에서 변경값 후보만 모은다(표시 문자열은 만들지 않는다).
//   direct     : override 레벨 단일값 — 스코프 전체에 걸리는 값(전체·워크센터 등)
//   targetVals : 대상별 값 + 그 대상의 기간별 값(MP 자원 그리드·기간 편집)
//   periodVals : override 레벨 기간별 값
// ov.value 만 보면 자원 그리드·기간별 override 는 값이 없는 것으로 보인다.
export function collectOverrideValues(ov) {
  const targetVals = [];
  const periodVals = [];
  (ov?.targets || []).forEach(tg => {
    if (hasEffectiveValue(null, tg?.value)) targetVals.push(tg.value);
    (tg?.periods || []).forEach(p => {
      if (hasEffectiveValue(null, p?.value)) targetVals.push(p.value);
    });
  });
  (ov?.periods || []).forEach(p => {
    if (hasEffectiveValue(null, p?.value)) periodVals.push(p.value);
  });
  return { direct: hasEffectiveValue(null, ov?.value) ? ov.value : null, targetVals, periodVals };
}

// RP/IP 스코프 행의 대상 코드·이름 필드명. 데이터 전용이라 표시 라벨은 담지 않는다.
// 재고 분류(inv_class)는 행에 이름 필드가 없어 코드로만 식별된다.
// 거점+품목(loc_item)은 두 컬럼을 함께 읽어야 대상 하나가 식별된다 — 한쪽만 보면
//   서로 다른 조합이 같은 대상으로 합쳐지고, 아예 안 읽으면 대상이 이름 없는 칸이 된다.
const SCOPE_ROW_FIELDS = {
  loc:       { cd: "loc_cd",       name: "loc_name" },
  item:      { cd: "item_cd",      name: "item_name" },
  item_grp:  { cd: "item_grp_cd",  name: "item_grp_name" },
  inv_class: { cd: "inv_class_cd", name: null },
  loc_item:  { cd: "loc_cd",       name: "loc_name", cd2: "item_cd", name2: "item_name" },
};

// 스코프 행 1건 → 대상 코드·이름. 조합 스코프는 두 값을 이어 하나의 대상으로 만든다.
function scopeRowTarget(ov, scope) {
  const f = SCOPE_ROW_FIELDS[scope];
  if (!f) return { cd: null, name: null };
  const join = (a, b) => [a, b].filter(v => v != null && v !== "").join(" / ") || null;
  const cd = f.cd2 ? join(ov[f.cd], ov[f.cd2]) : (ov[f.cd] ?? null);
  const name = f.name2 ? join(ov[f.name], ov[f.name2]) : (f.name ? (ov[f.name] ?? null) : null);
  return { cd, name };
}

// 변형 1건 → 파라미터별 변경 요약. override 스키마 세 갈래를 한 형태로 접는다.
//   1) 평탄 params        : { param_key: value }               (BF/DP/FP — overrides 키 자체가 없다)
//   2) 스코프 행 + params : { scope_type, loc_cd, …, params }   (RP/IP — 대상 1건이 행 1개)
//   3) 파라미터별 엔트리  : { param_key, scope_type, targets, value, … }  (MP)
// 같은 (파라미터, 스코프, 대상)은 한 칸으로 합치고, 값이 대상마다 다를 수 있어 값은 배열로 모은다.
// 버전 전역 파라미터는 백엔드 머지 규약상 모든 스코프 행에 같은 값이 복제돼 있다.
// 그것을 대상별로 세면 실제로는 일괄 적용인데 "거점 3곳 한정" 으로 읽히므로 전체 한 칸으로 접는다.
// MP 의 무효 override 제거(자원 능력 전체=무한)는 호출측이 먼저 걸러 넘긴다 —
//   이 파일이 MP 전용 함수를 import 하지 않기 위한 분담이다.
//
// @param variant      pickSeedVariant 결과
// @param defIndex     buildParamDefIndex 결과 — 표시 순서와 라벨 해석 가능 여부를 여기서 판정한다
// @param versionKeys  버전 전역 파라미터 키 Set (scenarioParamDefsFields.versionParamKeys)
// @param dropUnknown  defIndex 에 없는 키를 제외한다(기본 true) — 엔진 코드 원문 노출 방지
// @returns Array<{ paramKey, def, scopes: Array<{scopeType, cd, name, values}>, ovs }>
export function collectParamChangeRows(variant, { defIndex = {}, versionKeys, dropUnknown = true } = {}) {
  const rows = new Map();
  const known = (k) => !dropUnknown || !!defIndex[k];
  const rowOf = (k) => {
    if (!rows.has(k)) rows.set(k, { paramKey: k, def: defIndex[k] || null, scopes: [], ovs: [] });
    return rows.get(k);
  };
  const addScope = (row, scopeType, cd, name, value) => {
    const type = scopeType || "all";
    let cell = row.scopes.find(s => s.scopeType === type && (s.cd ?? null) === (cd ?? null));
    if (!cell) {
      cell = { scopeType: type, cd: cd ?? null, name: name || null, values: [] };
      row.scopes.push(cell);
    }
    if (!cell.name && name) cell.name = name;
    if (hasEffectiveValue(null, value)) cell.values.push(value);
  };

  for (const [k, v] of Object.entries(variant?.params || {})) {
    if (!hasEffectiveValue(null, v) || !known(k)) continue;
    addScope(rowOf(k), "all", null, null, v);
  }

  for (const ov of (Array.isArray(variant?.overrides) ? variant.overrides : [])) {
    if (!ov) continue;
    if (ov.param_key) {
      if (!known(ov.param_key)) continue;
      const row = rowOf(ov.param_key);
      row.ovs.push(ov);
      const { direct, periodVals } = collectOverrideValues(ov);
      const scope = ov.scope_type || "all";
      if (ov.targets?.length) {
        // 대상 칸의 값은 대상 자신의 값이 정본이고, 없으면 그 대상의 기간별 값, 그것도 없으면 override 값이다.
        // 값이 하나도 없는 대상도 칸은 만든다 — 공정 정책·우선순위 목록만 담은 override 의 적용 단위 표시용이다.
        ov.targets.forEach(tg => {
          const pv = (tg?.periods || []).map(p => p?.value).filter(v => hasEffectiveValue(null, v));
          if (hasEffectiveValue(null, tg?.value)) addScope(row, scope, tg?.cd, tg?.name, tg.value);
          else if (pv.length) pv.forEach(v => addScope(row, scope, tg?.cd, tg?.name, v));
          else addScope(row, scope, tg?.cd, tg?.name, direct);
        });
      } else {
        addScope(row, scope, null, null, direct);
        // 대상 없이 기간만 지정한 override 는 기간 값이 유일한 변경값 근거다.
        if (!hasEffectiveValue(null, direct)) periodVals.forEach(v => addScope(row, scope, null, null, v));
      }
      continue;
    }
    if (ov.params && typeof ov.params === "object") {
      const scope = ov.scope_type || "all";
      const tg = scopeRowTarget(ov, scope);
      for (const [k, v] of Object.entries(ov.params)) {
        if (!hasEffectiveValue(null, v) || !known(k)) continue;
        const row = rowOf(k);
        row.ovs.push(ov);
        if (versionKeys?.has(k)) { addScope(row, "all", null, null, v); continue; }
        addScope(row, scope, tg.cd, tg.name, v);
      }
    }
  }

  // 표시 순서는 카탈로그 param_defs 순으로 맞춘다 — 편집기 행 순서와 같아야 대조가 된다.
  // defIndex 에 없는 키(레거시 응답)는 등장 순서대로 뒤에 붙인다(sort 가 stable).
  const order = Object.keys(defIndex);
  const idx = (k) => { const i = order.indexOf(k); return i < 0 ? order.length : i; };
  return [...rows.values()].sort((a, b) => idx(a.paramKey) - idx(b.paramKey));
}
